import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import AuthLayout, { Field, inputStyle } from './AuthLayout';
import { useZh, useOpenSignup } from '../lib/useProduct';

const makeSchema = (zh: boolean) => z.object({
  email: z.string().email(zh ? '请输入有效的邮箱' : 'Enter a valid email'),
  password: z.string().min(8, zh ? '至少 8 个字符' : 'At least 8 characters'),
  passwordConfirm: z.string(),
  label: z.string().max(80).optional(),
}).refine((d) => d.password === d.passwordConfirm, {
  path: ['passwordConfirm'],
  message: zh ? '两次输入的密码不一致' : "Passwords don't match",
});
type FormVals = z.infer<ReturnType<typeof makeSchema>>;

export default function Register() {
  const zh = useZh();
  const openSignup = useOpenSignup();
  const [params] = useSearchParams();
  const inviteFromUrl = params.get('invite') || '';
  // Registration is allowed when signup is open, OR an invite link is present.
  const canRegister = openSignup || !!inviteFromUrl;
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormVals>({
    resolver: zodResolver(makeSchema(zh)),
  });

  async function onSubmit(values: FormVals) {
    setSubmitErr(null);
    const res = await fetch('/api/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: values.email,
        password: values.password,
        invite_code: inviteFromUrl,
        label: values.label || values.email.split('@')[0],
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      setSubmitErr(body.message || (zh ? ({
        invalid_invite: '邀请码无效、已过期或已用完。',
        invite_required: '注册需要邀请链接，请向管理员索取。',
        email_taken: '该邮箱已注册，请直接登录。',
        weak_password: '密码至少需要 8 个字符。',
        invalid_email: '邮箱格式看起来不对。',
      }[body.error as string] || '注册失败，请重试。') : ({
        invalid_invite: 'Invite code is invalid, expired, or fully used.',
        invite_required: 'You need an invite link to register. Ask the maintainer for one.',
        email_taken: 'An account with that email already exists. Try logging in.',
        weak_password: 'Password must be at least 8 characters.',
        invalid_email: "That doesn't look like a valid email.",
      }[body.error as string] || 'Could not register. Try again.')));
      return;
    }
    // Same stale-session hazard as Login: /profile is a locked route, and
    // navigating on the anonymous-cached session bounces the brand-new user
    // straight back off their own account. Refetch first.
    await qc.invalidateQueries();
    // Send brand-new tenants straight to Profile where the welcome
    // banner walks them through CV upload + auto-fill.
    navigate('/profile');
  }

  return (
    <AuthLayout
      title={zh ? '创建账号' : 'Create your account'}
      subtitle={inviteFromUrl
        ? (zh ? '欢迎——邀请有效。设置一个好记的密码。' : "Welcome — your invite is good. Pick a password you'll remember.")
        : openSignup
        ? (zh ? '免费注册，无需邀请码。' : 'Free to join — no invite code needed.')
        : (zh ? '注册需要邀请链接，请向管理员索取。' : 'You need an invite link to sign up. Ask the maintainer to send you one.')}
      footer={<>{zh ? '已有账号？' : 'Already have an account? '}<Link to="/login" style={{ color: 'var(--pri)' }}>{zh ? '登录' : 'Sign in'}</Link></>}
    >
      {!inviteFromUrl && !openSignup && (
        <div style={{ padding: 12, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, fontSize: 13, marginBottom: 20 }}>
          {zh ? '链接中没有邀请码。没有邀请无法注册。' : 'No invite code in URL. Without an invite you cannot register.'}
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Field label={zh ? '邮箱' : 'Email'} error={errors.email?.message}>
          <input type="email" autoFocus autoComplete="email" {...register('email')} style={inputStyle} />
        </Field>
        <Field label={zh ? '密码（至少 8 位）' : 'Password (8+ chars)'} error={errors.password?.message}>
          <input type="password" autoComplete="new-password" {...register('password')} style={inputStyle} />
        </Field>
        <Field label={zh ? '确认密码' : 'Confirm password'} error={errors.passwordConfirm?.message}>
          <input type="password" autoComplete="new-password" {...register('passwordConfirm')} style={inputStyle} />
        </Field>
        <Field label={zh ? '显示名称（选填）' : 'Display name (optional)'} error={errors.label?.message}>
          <input type="text" autoComplete="name" {...register('label')} style={inputStyle} placeholder={zh ? '默认取邮箱前缀' : 'defaults to email prefix'} />
        </Field>
        {submitErr && (
          <div style={{ padding: 10, background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13, marginBottom: 14, color: '#991b1b' }}>{submitErr}</div>
        )}
        <button type="submit" className="btn accent" style={{ width: '100%' }} disabled={isSubmitting || !canRegister}>
          {isSubmitting ? (zh ? '创建中…' : 'Creating account…') : (zh ? '创建账号' : 'Create account')}
        </button>
        {/* Notice at the point of collection — this form is where an email
            address and, shortly after, a CV get handed over. */}
        <p style={{ fontSize: 11.5, color: 'var(--ink3)', lineHeight: 1.6, marginTop: 12, marginBottom: 0 }}>
          {zh ? '创建账号即表示你已阅读并同意' : 'By creating an account you agree to the '}
          <Link to="/terms" style={{ color: 'var(--pri)' }}>{zh ? '《服务条款》' : 'Terms'}</Link>
          {zh ? '与' : ' and '}
          <Link to="/privacy" style={{ color: 'var(--pri)' }}>{zh ? '《隐私政策》' : 'Privacy Policy'}</Link>
          {zh ? '。' : '.'}
        </p>
      </form>
    </AuthLayout>
  );
}
